import React, { useState, useMemo } from 'react';
import { updateObjectSettings } from '../utils/storage';
import { computeDeliveryDate } from '../utils/inspectionStatus';
import { ModalTemplate } from './templates';
import './Modal.css';

// ── Built-in defaults (used when no DDL options are configured) ────
const DEFAULT_PERIOD_OPTIONS = [
  'No Deadline',
  '1 Week',
  '2 Weeks',
  '3 Weeks',
  '4 Weeks',
];

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * AssessmentSettingsModal
 * Allows configuring the reporting period (DDL-driven with built-in defaults)
 * which auto-calculates the delivery-date deadline for the assessment.
 */
export default function AssessmentSettingsModal({ object, ddlCollections = [], onClose }) {
  const [reportingPeriod, setReportingPeriod] = useState(object?.settings?.reportingPeriod || '2 Weeks');
  const [saving,          setSaving]          = useState(false);

  // DDL options — derived synchronously from pre-loaded collections (instant, no async wait)
  const ddlOptions = useMemo(() => {
    const col = ddlCollections.find((c) => {
      if (!c.assignedTo) return false;
      if (Array.isArray(c.assignedTo)) return c.assignedTo.includes('ddl_reportingPeriod');
      return c.assignedTo === 'ddl_reportingPeriod';
    });
    return (col?.items || []).map((it) => it.label);
  }, [ddlCollections]);

  // When DDL has options use those; otherwise fall back to the built-in defaults
  const periodOptions = ddlOptions.length > 0 ? ddlOptions : DEFAULT_PERIOD_OPTIONS;

  // Default is set in local state only — saved to DB only when user clicks Save

  // Compute delivery date from assessment start date + selected period
  const startDate    = object?.generalInfo?.startDate || null;
  const deliveryDate = computeDeliveryDate(startDate, reportingPeriod);

  // Build due date label
  let dueDateLabel = null;
  if (reportingPeriod) {
    if (/no\s*deadline/i.test(reportingPeriod)) {
      dueDateLabel = 'Not applicable';
    } else if (deliveryDate) {
      dueDateLabel = fmtDate(deliveryDate);
    } else if (!startDate) {
      dueDateLabel = 'No start date set — fill in Assessment Information first';
    }
  }

  async function handleSave() {
    if (!object) return;
    setSaving(true);
    try {
      await updateObjectSettings(object.id, { reportingPeriod });
      onClose();
    } catch (err) {
      console.error('Failed to save assessment settings:', err);
      setSaving(false);
    }
  }

  return (
    <ModalTemplate
      title="Assessment Settings"
      onClose={onClose}
      actions={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !object}>
            {saving ? 'Saving\u2026' : 'Save'}
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="assessment-reporting-period">Reporting Period</label>
        <select
          id="assessment-reporting-period"
          className="assign-select-full"
          value={reportingPeriod}
          onChange={(e) => setReportingPeriod(e.target.value)}
        >
          <option value="">— Select —</option>
          {periodOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        {ddlOptions.length === 0 && (
          <p className="assessment-settings-default-hint">
            Using built-in defaults. Add options to the &ldquo;Assessment &mdash; Reporting Period&rdquo; DDL collection to customise.
          </p>
        )}
      </div>

      {/* Delivery date — shown once a period is selected */}
      {reportingPeriod && (
        <div className="form-group">
          <label>Delivery Date</label>
          <div className="assessment-settings-delivery-date">
            {dueDateLabel || '—'}
          </div>
          {startDate && deliveryDate && (
            <p className="assessment-settings-delivery-hint">
              Calculated as: {fmtDate(startDate)} + {reportingPeriod}
            </p>
          )}
        </div>
      )}
    </ModalTemplate>
  );
}
